import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fechaLarga } from "@/lib/fechas";

const btn = "min-h-[44px] px-4 rounded-xl font-semibold text-base inline-flex items-center justify-center gap-2 disabled:opacity-60";

const Codigo = ({ c, onAnular }) => (
  <li className="py-3 flex items-center gap-3 border-t border-[#F2EDE4]" data-testid={`beta-codigo-${c.codigo}`}>
    <span className="flex-1 min-w-0">
      <span className="block font-mono font-bold text-base text-[#1C2421] whitespace-nowrap">{c.codigo}</span>
      <span className="block text-sm text-[#6E7A74]">Sin usar · caduca el {fechaLarga(c.caduca_en.slice(0, 10))}</span>
    </span>
    <button data-testid={`beta-copiar-${c.codigo}`} onClick={() => navigator.clipboard?.writeText(c.codigo).then(() => toast.success("Código copiado"))}
      className={`${btn} bg-[#F2EDE4] text-[#1C2421]`}><Icons.Copy size={18} /></button>
    <button data-testid={`beta-anular-${c.codigo}`} onClick={() => onAnular(c.codigo)} className={`${btn} bg-white border border-[#E5DFD5] text-[#B91C1C]`}>Anular</button>
  </li>
);

const Persona = ({ p, onCambiar }) => (
  <li className="py-3 flex items-center gap-3 border-t border-[#F2EDE4]" data-testid={`beta-persona-${p.email}`}>
    <span className="flex-1 min-w-0">
      <span className={`block font-semibold text-base break-all ${p.revocado ? "text-[#9AA39D] line-through" : "text-[#1C2421]"}`}>{p.email}</span>
      <span className="block text-sm text-[#6E7A74]">{p.revocado ? "Sin acceso" : p.origen === "codigo" ? `Con código ${p.codigo}` : "Lista inicial"}</span>
    </span>
    <button data-testid={`beta-${p.revocado ? "devolver" : "quitar"}-${p.email}`} onClick={() => onCambiar(p)}
      className={`${btn} ${p.revocado ? "bg-[#E8F0EC] text-[#2E5645]" : "bg-white border border-[#E5DFD5] text-[#B91C1C]"}`}>
      {p.revocado ? "Devolver acceso" : "Quitar acceso"}
    </button>
  </li>
);

// Perfil › Accesos de la beta (solo administrador): códigos de invitación y personas autorizadas.
export const AccesosBeta = () => {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState(null);
  const cargar = () => api.get("/admin/beta").then((r) => setDatos(r.data)).catch(() => toast.error("No se pudieron cargar los accesos."));
  useEffect(() => { if (abierto) cargar(); }, [abierto]);
  const accion = (fn, ok) => fn().then(() => { toast.success(ok); cargar(); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo hacer."));
  const crear = () => accion(() => api.post("/admin/beta/codigos"), "Código creado: cópialo y envíaselo");
  const anular = (codigo) => accion(() => api.delete(`/admin/beta/codigos/${codigo}`), "Código anulado");
  const cambiar = (p) => accion(() => api.post(`/admin/beta/${p.revocado ? "devolver" : "quitar"}`, { email: p.email }), p.revocado ? "Acceso devuelto" : "Acceso quitado");
  return (
    <div className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm" data-testid="accesos-beta">
      <button data-testid="accesos-beta-toggle" onClick={() => setAbierto((v) => !v)} className="w-full flex items-center gap-2 text-left">
        <Icons.KeyRound size={20} className="text-[#3A6B56]" />
        <span className="flex-1 font-heading text-lg font-bold text-[#1C2421]">Accesos de la beta</span>
        {abierto ? <Icons.ChevronUp size={20} className="text-[#9AA39D]" /> : <Icons.ChevronDown size={20} className="text-[#9AA39D]" />}
      </button>
      {abierto && (!datos ? <p className="text-base text-[#6E7A74] mt-3">Cargando…</p> : <>
        {!datos.activa && <p className="mt-3 text-sm font-semibold text-[#92400E] bg-[#FFFBEB] rounded-xl p-3" data-testid="accesos-beta-inactiva">La beta privada está desactivada aquí: ahora puede entrar cualquiera.</p>}
        <p className="text-base text-[#4A5550] mt-3">Crea un código para cada persona. Sirve una sola vez y caduca a los 7 días si nadie lo usa. La persona lo escribe al crear su cuenta.</p>
        <button data-testid="beta-crear-codigo" onClick={crear} className={`${btn} w-full mt-3 bg-[#3A6B56] text-white`}><Icons.Plus size={18} /> Crear código de invitación</button>
        <p className="font-semibold text-[#1C2421] mt-5">Códigos pendientes ({datos.codigos.length})</p>
        <ul data-testid="beta-codigos">{datos.codigos.map((c) => <Codigo key={c.codigo} c={c} onAnular={anular} />)}</ul>
        <p className="font-semibold text-[#1C2421] mt-5">Personas autorizadas</p>
        <ul data-testid="beta-personas">{datos.personas.map((p) => <Persona key={p.email} p={p} onCambiar={cambiar} />)}</ul>
        <p className="text-xs text-[#9AA39D] mt-3">Quitar el acceso no borra la cuenta ni sus datos. Tu cuenta de administrador siempre tiene acceso.</p>
      </>)}
    </div>
  );
};
